import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, Container, IconButton, Link, Stack, styled, Typography } from '@mui/material'
import capitalize from 'lodash/capitalize'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { trySaveToLocalStorage } from '@omnea-app/fe-browser-utils/trySaveToLocalStorage'

const PromotionalBannerBox = styled(Box)(({ theme }) => ({
  height: '98px',
  maxWidth: '1116px',
  backgroundImage: 'url(/app/assets/promotional-banner-background.svg)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  borderTopRightRadius: '8px',
  borderTopLeftRadius: '8px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 16px',
  [theme.breakpoints.down('md')]: {
    height: 'auto',
    flexDirection: 'column',
    padding: '16px',
    gap: '12px',
    alignItems: 'flex-start',
  },
}))

const BANNER_DISMISSED_KEY = 'omnea-supplier-banner-dismissed'

export const PromotionalBanner = () => {
  const { t } = useTranslation('supplier-dashboard')
  const { user, org } = useAuthenticatedUser()

  const [isVisible, setIsVisible] = useState(true)

  useEffect(() => {
    const dismissed = sessionStorage.getItem(BANNER_DISMISSED_KEY)
    if (dismissed) {
      setIsVisible(false)
    }
  }, [])

  const handleDismiss = () => {
    trySaveToLocalStorage({ key: BANNER_DISMISSED_KEY, value: 'true' })
    setIsVisible(false)
  }

  const handleLearnMoreClicked = () => {
    track('SupplierPortalBannerClicked', {
      name: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`,
      email: user?.email,
      supplierName: user?.email.split('@')[1] ?? '',
      customerName: org?.name,
    })
  }

  if (!isVisible) return null

  return (
    <Container>
      <PromotionalBannerBox>
        <Stack
          direction="row"
          alignItems="center"
          spacing={4}
          sx={(theme) => ({
            [theme.breakpoints.down('md')]: {
              gap: theme.spacing(2),
            },
          })}
        >
          <Image src="/logo-without-text.svg" alt="logo" height={30} width={50} />
          <Stack direction="column" spacing={2}>
            <Typography variant="upsized-semiBold" sx={{ color: (theme) => theme.palette.neutralLight[0] }}>
              {t('supplier-banner.title')}
            </Typography>
            <Typography variant="small-regular" sx={{ color: (theme) => theme.palette.neutralLight[0] }}>
              {t('supplier-banner.description')}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={3}
          sx={(theme) => ({
            [theme.breakpoints.down('md')]: {
              gap: theme.spacing(2),
            },
          })}
        >
          <Link onClick={handleLearnMoreClicked} href="https://omnea.co" target="_blank" rel="noopener noreferrer">
            <Button variant="outlined" size="large" color="primary">
              {capitalize(t('supplier-banner.learn-more'))}
            </Button>
          </Link>
          <IconButton onClick={handleDismiss}>
            <CloseIcon sx={{ color: 'neutral.main' }} />
          </IconButton>
        </Stack>
      </PromotionalBannerBox>
    </Container>
  )
}
